/* eslint-disable camelcase */
import React, { useMemo, useCallback } from 'react';
import Head from 'next/head';
import { useTranslation } from 'next-i18next';
import { getCategImageWebp } from '@/utils/categUtils';
import { HtmlEntities } from '@/utils/HtmlEntities';
import { langOrFullLocale, getTwitterUsername, sanitizeUrl } from '@/utils/millesimaUtils';
import { convertToWebPUrl, getProductImage } from '@/utils/productUtils';

import { NS } from '@/constants';
import { useFeature } from '@/hooks';
import { CategModel, HrefLangModel, ProductModel } from '@/models';
import { useAppSelector } from '@/redux';
import { FEATURES } from '@/constants/features';
import { PAGE_TYPE } from '@/constants/datalayer';
import { URLS } from '@/constants/urls';

type MetaTagsMillesimaProps = {
  product?: ProductModel;
  categ?: CategModel;
  home?: boolean;
  searchMode?: boolean;
  categListing?: boolean;
  landing?: boolean;
  title?: string;
  metaDesc?: string;
  privateWebsite?: boolean;
  isMobileDevice?: boolean;
  originalUrl?: string;
  nbTotalRows?: number;
  pageSize?: number;
  page?: number;
  item?: string;
  imgSrcToPreload?: string[];
  hrefLang?: HrefLangModel[];
  isSpirit?: boolean;
  defaultTag?: boolean;
  noIndex?: boolean;
};

export const MetaTagsMillesima = ({
  product, categ, home, searchMode, categListing, landing, title, metaDesc,
  privateWebsite, isMobileDevice, originalUrl, nbTotalRows, pageSize, page, item, imgSrcToPreload = [],
  hrefLang, isSpirit = false, defaultTag, noIndex
}: MetaTagsMillesimaProps) => {
  const storeConf = useAppSelector(({ root }) => root.storeConf);
  const isSuffixEnabled = useFeature(storeConf, FEATURES.ADD_SUFFIX_META);
  const titleSuffix = isSuffixEnabled ? ` - ${storeConf?.shortUrl ?? 'Millesima'}` : '';
  const { t } = useTranslation(NS.COMMON);
  const fb_app_id = useMemo(() => storeConf?.fb_app_id ?? '0', [storeConf]);

  const renderImgSrcToPreload = () => (
    imgSrcToPreload.map((srcToPreload, key) => (
      <link key={key} rel="preload" as="image" href={srcToPreload} />
    ))
  );

  const renderSearchModeTags = useCallback(() => (
    <Head>
      <title>{t('search.title')}</title>
      <meta name="type" content={PAGE_TYPE.SEARCH} />
    </Head>
  ), []);

  const renderNoindexMeta = useCallback(() => (
    <>
      <meta name="robots" content="noindex" />
      <meta name="googlebot" content="noindex" />
    </>
  ), []);

  const renderProductTags = useMemo(() => {
    if (product) {
      const { title, seoKeyword, metaDesc, image, exclusive_cellar, hrefLang } = product;
      const twitterUsername = getTwitterUsername(storeConf.twitter);
      const ogImage = getProductImage(image, false, 'full');

      let langs = [];

      const pageUrl = seoKeyword ? sanitizeUrl(storeConf.url, seoKeyword, isSpirit) : '';
      const pageDesc = HtmlEntities.decode(metaDesc);
      const pageTitle = HtmlEntities.decode(title);
      const pageTitleSuffixed = pageTitle + titleSuffix;
      const productItems = (product.itemsCP || []).concat(product.items || []);
      const itemFromUrl = productItems.find((i) => item?.startsWith(i.partnumber));

      if (itemFromUrl?.hrefLang?.length) {
        langs = itemFromUrl.hrefLang;
      } else if (hrefLang?.length) {
        langs = hrefLang;
      }

      return (
        <Head>
          <title data-test-id="title">{pageTitleSuffixed}</title>
          <meta name="theme-color" content="#000000" />
          <meta name="type" content={PAGE_TYPE.PRODUCT} />
          {pageDesc && <meta name="description" content={pageDesc} />}
          {exclusive_cellar && renderNoindexMeta()}

          {/*
            Fragments in Head cannot be wrapped in a custom component.
            See https://nextjs.org/docs/api-reference/next/head
          */}
          {twitterUsername && (
            <>
              <meta name="twitter:card" content="summary_large_image" />
              <meta name="twitter:site" content={twitterUsername} />
              <meta name="twitter:title" content={pageTitle} />
              <meta name="twitter:description" content={pageDesc} />
              <meta name="twitter:image" content={URLS.DEFAULT_OG_IMAGE} />
            </>
          )}

          {pageUrl && (
            <>
              <link rel="canonical" href={pageUrl} />
              {!langs.find((lang) => lang.lng === storeConf.locale) && (
                <link rel="alternate" href={pageUrl} hrefLang={langOrFullLocale(storeConf.locale)} />
              )}
            </>
          )}

          {langs.map((lang, key) => (
            <link key={key} rel="alternate" href={lang.url} hrefLang={langOrFullLocale(lang.lng)} />
          ))}

          <link
            rel="preload"
            as="image"
            href={convertToWebPUrl(getProductImage(image, false, 'thumb'))}
            media="(max-width: 768px)"
          />

          {renderImgSrcToPreload()}

          <meta property="og:title" content={pageTitle} />
          <meta property="og:url" content={pageUrl} />
          <meta property="og:description" content={pageDesc} />
          <meta property="og:type" content="product" />
          <meta property="og:image" content={ogImage} />
          <meta property="fb:app_id" name="fb_app_id" content={fb_app_id} />
          <meta name="pageGroup" content="Product" />
        </Head>
      );
    }

    return null;
  }, [product, originalUrl]);

  const renderCategTags = useMemo(() => {
    if (categ) {
      const twitterUsername = getTwitterUsername(storeConf.twitter);
      const { pageTitle: title, metaDescription, urlKeyword, hrefLang, isPrivate, parentGroupIdentifier, image, ogImage } = categ;
      const langs = hrefLang?.length > 0 ? hrefLang : [];
      const pageDesc = HtmlEntities.decode(metaDescription);
      const pageTitle = HtmlEntities.decode(title);
      const nbTotalRowsInt = +nbTotalRows;
      const pageSizeInt = +pageSize;
      const pageInt = +page;
      const pageTitleSuffixed = pageTitle + titleSuffix;
      const categImage = getCategImageWebp(categ.image);
      let urlPrev;
      let urlNext;

      if (pageInt > 1) {
        const pageParam = pageInt > 2 ? `?page=${pageInt - 1}` : '';
        urlPrev = `${urlKeyword}${pageParam}`;
      }
      if (nbTotalRowsInt > pageInt * pageSizeInt) {
        urlNext = `${urlKeyword}?page=${pageInt + 1}`;
      }

      const canonical = pageInt > 1 ? `${urlKeyword}?page=${pageInt}` : urlKeyword;

      return (
        <Head>
          <title>{pageTitleSuffixed}</title>
          {!isMobileDevice && categImage && (
            <link rel="preload" as="image" href={categImage} />
          )}

          {renderImgSrcToPreload()}

          {isPrivate && renderNoindexMeta()}
          {urlNext && <link rel="next" href={urlNext} />}
          {urlPrev && <link rel="prev" href={urlPrev} />}
          <meta name="type" content={PAGE_TYPE.CATEG} />
          <meta name="parentGroupIdentifier" content={parentGroupIdentifier} />

          {/*
            Fragments in Head cannot be wrapped in a custom component.
            See https://nextjs.org/docs/api-reference/next/head
          */}
          {twitterUsername && (
            <>
              <meta name="twitter:card" content="summary_large_image" />
              <meta name="twitter:site" content={twitterUsername} />
              <meta name="twitter:title" content={pageTitle} />
              <meta name="twitter:description" content={pageDesc} />
              <meta name="twitter:image" content={ogImage || URLS.DEFAULT_OG_IMAGE} />
            </>
          )}

          {pageDesc && <meta name="description" content={pageDesc} />}
          {urlKeyword && (
            <>
              <link rel="canonical" href={sanitizeUrl(storeConf.url, canonical, isSpirit)} />
              {!langs.find((l) => l.lng === storeConf.locale) && (
                <link rel="alternate" href={sanitizeUrl(storeConf.url, canonical, isSpirit)}
                  hrefLang={langOrFullLocale(storeConf.locale)} />
              )}
            </>
          )}

          {langs.map((lang) => (
            <link key={lang.lng} rel="alternate" href={lang.url} hrefLang={langOrFullLocale(lang.lng)} />
          ))}

          <meta property="og:image" content={ogImage || URLS.DEFAULT_OG_IMAGE} />
          <meta property="fb:app_id" name="fb_app_id" content={fb_app_id} />
        </Head>
      );
    }

    return null;
  }, [categ, nbTotalRows, pageSize, page, originalUrl]);

  const renderHomeTags = useMemo(() => {
    if (metaDesc && title) {
      const pageDesc = HtmlEntities.decode(metaDesc);
      const pageTitle = HtmlEntities.decode(title);
      const pageTitleSuffixed = pageTitle + titleSuffix;

      return (
        <Head>
          <title>{pageTitleSuffixed}</title>

          <meta name="description" content={pageDesc}></meta>
          <meta name="theme-color" content="#000000" />
          <meta name="type" content={PAGE_TYPE.HOME_PAGE} />

          <link rel="canonical" href={storeConf.url} />
          <link rel="alternate" href={storeConf.url} hrefLang={langOrFullLocale(storeConf.locale)} />
          {!privateWebsite && storeConf.otherStores?.map((loc) => (
            <link key={loc.storeId} rel="alternate" href={loc.url} hrefLang={langOrFullLocale(loc.locale)} />
          ))}
          {renderImgSrcToPreload()}
          <meta name="twitter:image" content={URLS.DEFAULT_OG_IMAGE} />
          <meta name="og:image" content={URLS.DEFAULT_OG_IMAGE} />
        </Head>
      );
    }

    return null;
  }, [metaDesc, title]);

  const renderDefaultTags = useMemo(() => {
    return (
      <Head>
        <title>{title + titleSuffix}</title>
        <meta name="description" content={metaDesc ?? storeConf.description} />
      </Head>
    );
  }, [defaultTag]);

  const renderCategListingTags = useMemo(() => {
    const pageDesc = HtmlEntities.decode(metaDesc);
    const pageTitle = HtmlEntities.decode(title);
    const pageTitleSuffixed = pageTitle + titleSuffix;
    const sanitizedUrl = sanitizeUrl(storeConf.url, originalUrl);
    const langs = hrefLang?.length > 0 ? hrefLang : [];
    return (
      <Head>
        <title>{pageTitleSuffixed}</title>
        {pageDesc && <meta name="description" content={pageDesc}></meta>}
        <meta name="theme-color" content="#000000" />
        <meta name="type" content={PAGE_TYPE.CATEG_LISTING} />
        {renderImgSrcToPreload()}
        {sanitizedUrl && (
          <>
            <link rel="canonical" href={sanitizedUrl} />
            {!langs.find((lang) => lang.lng === storeConf.locale) && (
              <link rel="alternate" href={sanitizedUrl} hrefLang={langOrFullLocale(storeConf.locale)} />
            )}
            {langs.map((lang) => (
              <link key={lang.lng} rel="alternate" href={lang.url} hrefLang={langOrFullLocale(lang.lng)} />
            ))}
          </>
        )}
      </Head>
    );
  }, [metaDesc, title, originalUrl, hrefLang]);

  const renderLandingTag = useMemo(() => {
    const pageDesc = HtmlEntities.decode(metaDesc);
    const pageTitle = HtmlEntities.decode(title);
    const pageTitleSuffixed = pageTitle + titleSuffix;
    const sanitizedUrl = sanitizeUrl(storeConf.url, originalUrl);
    const langs = hrefLang?.length > 0 ? hrefLang : [];
    return (
      <Head>
        <title>{pageTitleSuffixed}</title>
        {pageDesc && <meta name="description" content={pageDesc} />}
        {noIndex && (
          <meta name="robots" content="noindex" />
        )}
        {sanitizedUrl && (
          <>
            <link rel="canonical" href={sanitizedUrl} />
            {!langs.find((lang) => lang.lng === storeConf.locale) && (
              <link rel="alternate" href={sanitizedUrl} hrefLang={langOrFullLocale(storeConf.locale)} />
            )}
            {langs.map((lang) => (
              <link key={lang.lng} rel="alternate" href={lang.url} hrefLang={langOrFullLocale(lang.lng)} />
            ))}
          </>
        )}
      </Head>
    );
  }, [metaDesc, title, originalUrl, hrefLang]);

  return searchMode ? renderSearchModeTags() :
    product ? renderProductTags :
      categ ? renderCategTags :
        home ? renderHomeTags :
          categListing ? renderCategListingTags :
            landing ? renderLandingTag :
              defaultTag ? renderDefaultTags :
                null;
};

export default MetaTagsMillesima;
